import { useLocation, useNavigate } from "react-router";
import { useEffect, useMemo, useState } from "react";
import {
  LayoutDashboard,
  Users,
  Receipt,
  Calculator,
  List,
  Clock,
  Shield,
  ChevronDown,
  ChevronRight,
  KeyRound,
  UserCog,
  ScrollText,
  PanelLeftClose,
  PanelLeft,
} from "lucide-react";
import { selectAuth, useAppSelector } from "../../store";
import {
  PRIVILEGE_CREATE_NEW_PRIVILEGE,
  PRIVILEGE_CREATE_NEW_ROLE,
  PRIVILEGE_MANUALLY_RUN_INVOICE_CREATION_JOB,
  PRIVILEGE_VIEW_ASSIGNED_ROLE_PRIVILEGES,
  PRIVILEGE_VIEW_RECENT_USER_ACTIVITY,
  hasPrivilege,
} from "../../../redux/utils/authPrivileges";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

const SIDEBAR_COLLAPSED_STORAGE_KEY = "hib-billing-sidebar-collapsed";
const SIDEBAR_WIDTH_EXPANDED = "16rem";
const SIDEBAR_WIDTH_COLLAPSED = "4.5rem";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/clients", label: "Clients", icon: Users },
  { path: "/invoices", label: "Invoices", icon: Receipt },
  { path: "/accounting-allocation", label: "Accounting Allocation", icon: Calculator },
  { path: "/service-catalog", label: "Offer Catalog", icon: List },
  {
    path: "/user-activity",
    label: "Recent User Activity",
    icon: Clock,
    privilege: PRIVILEGE_VIEW_RECENT_USER_ACTIVITY,
  },
];

const navGroups = [
  {
    label: "Administration",
    icon: Shield,
    children: [
      {
        path: "/admin/automated-invoice-job",
        label: "Automated Invoice Create Job",
        icon: Receipt,
        privilege: PRIVILEGE_MANUALLY_RUN_INVOICE_CREATION_JOB,
      },
      {
        path: "/role-privileges",
        label: "Assigned Role Privileges",
        privilege: PRIVILEGE_VIEW_ASSIGNED_ROLE_PRIVILEGES,
      },
      {
        path: "/roles/create",
        label: "Create New Role",
        icon: UserCog,
        privilege: PRIVILEGE_CREATE_NEW_ROLE,
      },
      {
        path: "/role-privileges/create",
        label: "Create New Privilege",
        icon: KeyRound,
        privilege: PRIVILEGE_CREATE_NEW_PRIVILEGE,
      },
      {
        path: "/admin/server-logs",
        label: "Server Logs",
        icon: ScrollText,
        privilege: PRIVILEGE_VIEW_ASSIGNED_ROLE_PRIVILEGES,
      },
    ],
  },
];

function isPathActive(pathname, itemPath) {
  return pathname === itemPath;
}

function visibleNavGroup(group, decodedToken) {
  return visibleGroupChildren(group, decodedToken).length > 0;
}

function visibleGroupChildren(group, decodedToken) {
  return group.children.filter(
    (child) => !child.privilege || hasPrivilege(decodedToken, child.privilege),
  );
}

function navLinkClass(isActive, size = "md") {
  return [
    "nav-item",
    size === "sm" ? "nav-item-sm" : "",
    isActive ? "nav-item-active" : "nav-item-idle",
  ]
    .filter(Boolean)
    .join(" ");
}

function navGroupClass(groupActive) {
  return `nav-item ${groupActive ? "nav-group-active" : "nav-item-idle"}`;
}

function readStoredCollapsed() {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function applySidebarWidth(collapsed) {
  document.documentElement.style.setProperty(
    "--app-sidebar-width",
    collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
  );
}

function SidebarNavTooltip({ collapsed, label, children }) {
  if (!collapsed) {
    return children;
  }
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={10}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

export const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const decodedToken = useAppSelector(selectAuth).decodedToken;
  const [collapsed, setCollapsed] = useState(() => {
    const value = readStoredCollapsed();
    if (typeof document !== "undefined") {
      applySidebarWidth(value);
    }
    return value;
  });
  const [expandedGroups, setExpandedGroups] = useState(() => new Set(["Administration"]));

  const visibleNavItems = navItems.filter(
    (item) => !item.privilege || hasPrivilege(decodedToken, item.privilege),
  );

  const visibleNavGroups = useMemo(
    () => navGroups.filter((group) => visibleNavGroup(group, decodedToken)),
    [decodedToken],
  );

  useEffect(() => {
    applySidebarWidth(collapsed);
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, collapsed ? "1" : "0");
    } catch {
      /* ignore quota / private mode */
    }
  }, [collapsed]);

  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      for (const group of visibleNavGroups) {
        const groupActive = visibleGroupChildren(group, decodedToken).some((child) =>
          isPathActive(location.pathname, child.path),
        );
        if (groupActive) {
          next.add(group.label);
        }
      }
      return next;
    });
  }, [location.pathname, visibleNavGroups, decodedToken]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => !prev);
  };

  const toggleGroup = (label) => {
    if (collapsed) {
      setCollapsed(false);
      setExpandedGroups((prev) => new Set(prev).add(label));
      return;
    }
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  return (
    <div className={`sidebar-shell${collapsed ? " is-collapsed" : ""}`}>
      <div aria-hidden="true" className="sidebar-elevation" />
      <aside className="sidebar-panel" aria-label="Main navigation">
        <div aria-hidden="true" className="sidebar-inner-glow" />
        <div aria-hidden="true" className="sidebar-edge-left" />
        <div aria-hidden="true" className="sidebar-edge-top" />
        <div aria-hidden="true" className="sidebar-glass-edge" />
        <div aria-hidden="true" className="sidebar-glass-edge-line" />
        <div className="sidebar-header">
          <div className="sidebar-header-brand">
            <img
              src={`${process.env.PUBLIC_URL}/hibbert-logo-on-dark.svg`}
              alt="Hibbert Group"
              className="sidebar-brand-logo"
            />
          </div>
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="app-btn-plain sidebar-collapse-btn"
                onClick={toggleCollapsed}
                aria-expanded={!collapsed}
                aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {collapsed ? (
                  <PanelLeft className="h-4 w-4" />
                ) : (
                  <PanelLeftClose className="h-4 w-4" />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={10}>
              {collapsed ? "Expand menu" : "Collapse menu"}
            </TooltipContent>
          </Tooltip>
        </div>
        <nav className="sidebar-nav">
          <ul className="min-w-0 space-y-1">
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = isPathActive(location.pathname, item.path);
              return (
                <li key={item.path}>
                  <SidebarNavTooltip collapsed={collapsed} label={item.label}>
                    <button
                      type="button"
                      onClick={() => {
                        if (location.pathname !== item.path) {
                          navigate(item.path);
                        }
                      }}
                      className={navLinkClass(isActive)}
                      aria-label={item.label}
                    >
                      <Icon className="h-5 w-5 shrink-0" />
                      <span className="sidebar-nav-label min-w-0 truncate">{item.label}</span>
                    </button>
                  </SidebarNavTooltip>
                </li>
              );
            })}

            {visibleNavGroups.map((group) => {
              const GroupIcon = group.icon;
              const expanded = !collapsed && expandedGroups.has(group.label);
              const children = visibleGroupChildren(group, decodedToken);
              const groupActive = children.some((child) =>
                isPathActive(location.pathname, child.path),
              );
              if (children.length === 0) {
                return null;
              }
              return (
                <li key={group.label}>
                  <SidebarNavTooltip collapsed={collapsed} label={group.label}>
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.label)}
                      className={navGroupClass(groupActive)}
                      aria-expanded={expanded}
                      aria-label={group.label}
                    >
                      <GroupIcon className="h-5 w-5 shrink-0" />
                      <span className="sidebar-nav-label min-w-0 flex-1 truncate">
                        {group.label}
                      </span>
                      {expanded ? (
                        <ChevronDown className="sidebar-nav-chevron h-4 w-4 shrink-0" />
                      ) : (
                        <ChevronRight className="sidebar-nav-chevron h-4 w-4 shrink-0" />
                      )}
                    </button>
                  </SidebarNavTooltip>
                  {expanded ? (
                    <ul className="sidebar-nav-group-children mt-1 min-w-0 space-y-1 pl-4">
                      {children.map((child) => {
                        const ChildIcon = child.icon ?? group.icon;
                        const isActive = isPathActive(location.pathname, child.path);
                        return (
                          <li key={child.path}>
                            <button
                              type="button"
                              onClick={() => {
                                if (location.pathname !== child.path) {
                                  navigate(child.path);
                                }
                              }}
                              className={navLinkClass(isActive, "sm")}
                            >
                              <ChildIcon className="h-4 w-4 shrink-0" />
                              <span className="sidebar-nav-label min-w-0 truncate">
                                {child.label}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>
    </div>
  );
};
